// SMART DEMO — Sektör Config Registry (FAZ 4A)
// 12 sektörü tek ortak config kaynağına alır.
// Bu dosya saf veridir; React/lucide import etmez.
//
// Kullanım:
//   Wizard sector select  -> SECTOR_CONFIGS
//   Service selection     -> sectorConfig.serviceOptions
//   CTA recommendation    -> sectorConfig.defaultCtas
//
// Mevcut çalışan Beauty V2 simulator flow'u bu dosyaya dokunmaz;
// beauty akışı flowBuilder içinde aynen korunur.

export type CtaDestination = "whatsapp" | "website" | "instagram" | "phone";

/* Canonical aksiyon niyeti — label parse edilmeden id/intent üzerinden davranış belirlenir. */
export type CtaIntent =
  | "appointment"
  | "order"
  | "quote"
  | "consultation"
  | "business_model"
  | "info"
  | "product_info"
  | "question"
  | "whatsapp"
  | "phone"
  | "website"
  | "instagram";

export interface SectorCta {
  id: string;              // CTA_POOL anahtarıyla eşleşebilen benzersiz id
  label: string;           // Görünen etiket (emoji içerebilir)
  prefill: string;         // WhatsApp önceden doldurulmuş mesaj
  destination: CtaDestination;
  intent?: CtaIntent;      // canonical davranış; boşsa id→intent mapping kullanılır
}

export interface SectorConfig {
  id: string;
  label: string;
  description?: string;
  serviceOptions: string[];
  minServices: number;
  maxServices: number;
  defaultCtas: SectorCta[];
  optionalCtas: SectorCta[];
  supportsCampaigns: boolean;
  supportsVideos: boolean;
  recommendedVideoCount?: number;
  customServiceLabel: string;
}

/* ---------------- Ortak kurallar ---------------- */

export const SERVICE_RULES = { min: 1, max: 5 } as const;

export const MEDIA_RULES = {
  minImagesPerService: 3,
  maxImagesPerService: 10,
  maxVideos: 2,
  recommendedVideos: 2,
} as const;

export const CAMPAIGN_RULES = { min: 0, max: 5 } as const;

export const CTA_RULES = { max: 3 } as const;

/* ---------------- CTA master pool ---------------- */

export const CTA_POOL: SectorCta[] = [
  { id: "appointment", label: "Randevu Oluştur", prefill: "Merhaba, randevu oluşturmak istiyorum.", destination: "whatsapp" },
  { id: "quote", label: "Teklif İste", prefill: "Merhaba, teklif almak istiyorum.", destination: "whatsapp" },
  { id: "question", label: "Soru Sor", prefill: "Merhaba, birkaç soru sormak istiyorum.", destination: "whatsapp" },
  { id: "whatsapp", label: "WhatsApp'tan İletişime Geç", prefill: "Merhaba, iletişime geçmek istiyorum.", destination: "whatsapp" },
  { id: "website", label: "Web Sitesini İncele", prefill: "", destination: "website" },
  { id: "order", label: "Sipariş Ver", prefill: "Merhaba, sipariş vermek istiyorum.", destination: "whatsapp" },
  { id: "phone", label: "Telefonla Ara", prefill: "", destination: "phone" },
  { id: "instagram", label: "Instagram'dan İncele", prefill: "", destination: "instagram" },
];

/* ---------------- CTA intent config ---------------- */

/* id → canonical intent eşlemesi. Yalnızca legacy fallback; aktif SECTOR_CONFIGS
   CTA'ları intent'i explicit taşır, bu eşleme çalışma zamanında kullanılmaz. */
export const CTA_INTENT_BY_ID: Record<string, CtaIntent> = {
  appointment: "appointment",
  order: "order",
  quote: "quote",
  consultation: "consultation",
  business_model: "business_model",
  info: "info",
  question: "question",
  whatsapp: "whatsapp",
  phone: "phone",
  website: "website",
  instagram: "instagram",
};

/* Bir CTA'nın canonical intent'ini çözer. Açıkça intent tanımlıysa onu,
   yoksa id→intent eşlemesini kullanır. Label'a hiç bakmaz. */
export function resolveCtaIntent(cta: Pick<SectorCta, "id" | "intent">): CtaIntent {
  return cta.intent ?? CTA_INTENT_BY_ID[cta.id] ?? "question";
}

/* Her intent için puan + deterministik sinyal kopyası. Tek kaynak, random YOK. */
export const CTA_INTENT_CONFIG: Record<
  CtaIntent,
  { score: number; signalTitle: string; signalDescription: string }
> = {
  appointment: {
    score: 5,
    signalTitle: "Yüksek randevu niyeti",
    signalDescription: "Müşteri doğrudan randevu oluşturma adımını seçti.",
  },
  order: {
    score: 5,
    signalTitle: "Yüksek satın alma niyeti",
    signalDescription: "Müşteri sipariş verme adımını seçti.",
  },
  quote: {
    score: 4,
    signalTitle: "Teklif talebi oluştu",
    signalDescription: "Müşteri fiyat ve teklif almak istedi.",
  },
  consultation: {
    score: 4,
    signalTitle: "Görüşme niyeti algılandı",
    signalDescription: "Müşteri doğrudan bir danışmanla görüşmek istedi.",
  },
  business_model: {
    score: 4,
    signalTitle: "İş modeli ilgisi algılandı",
    signalDescription: "Müşteri çalışma ve kazanç modeli hakkında detay istedi.",
  },
  info: {
    score: 3,
    signalTitle: "Detaylı bilgi ilgisi algılandı",
    signalDescription: "Müşteri hizmet veya seçenekler hakkında daha fazla bilgi almak istedi.",
  },
  product_info: {
    score: 3,
    signalTitle: "Ürün ilgisi algılandı",
    signalDescription: "Müşteri ürünler hakkında daha fazla bilgi istedi.",
  },
  question: {
    score: 3,
    signalTitle: "İletişim niyeti algılandı",
    signalDescription: "Müşteri doğrudan soru sormak istedi.",
  },
  whatsapp: {
    score: 3,
    signalTitle: "İletişim niyeti algılandı",
    signalDescription: "Müşteri WhatsApp üzerinden iletişime geçmek istedi.",
  },
  phone: {
    score: 4,
    signalTitle: "Doğrudan görüşme niyeti",
    signalDescription: "Müşteri telefonla iletişim kurmak istedi.",
  },
  website: {
    score: 2,
    signalTitle: "İnceleme ilgisi algılandı",
    signalDescription: "Müşteri web sitesi veya mağazayı incelemek istedi.",
  },
  instagram: {
    score: 2,
    signalTitle: "Sosyal profil ilgisi",
    signalDescription: "Müşteri Instagram hesabını incelemek istedi.",
  },
};

/* ---------------- 12 sektör ---------------- */

export const SECTOR_CONFIGS: SectorConfig[] = [
  {
    id: "beauty",
    label: "Güzellik & Bakım",
    description: "Güzellik salonu, cilt bakımı ve kişisel bakım hizmetleri.",
    serviceOptions: [
      "Lazer Epilasyon",
      "Cilt Bakımı",
      "Hydrafacial / Yeni Nesil Cilt Bakımı",
      "Kalıcı Makyaj",
      "Microblading / Kaş Tasarımı",
      "İpek Kirpik",
      "Kirpik Lifting",
      "Kaş Laminasyonu",
      "Bölgesel İncelme",
      "G5 / Selülit Bakımı",
      "Dudak Renklendirme",
      "Dudak Vitamini / Bakımı",
      "Profesyonel Makyaj",
      "Protez Tırnak / Nail Art",
      "Manikür / Pedikür",
      "Saç Bakımı / Kuaför Hizmetleri",
      "Diğer / Özel Hizmet",
    ],
    minServices: 1,
    maxServices: 5,
    defaultCtas: [
      {
        id: "appointment",
        label: "📅 Hemen Randevu Oluştur",
        intent: "appointment",
        prefill: "Merhaba, kampanyalarınızı ve çalışmalarınızı inceledim. Randevu oluşturmak istiyorum.",
        destination: "whatsapp",
      },
      {
        id: "question",
        label: "💬 Soru Sormak İstiyorum",
        intent: "question",
        prefill: "Merhaba, işlemleriniz hakkında birkaç soru sormak istiyorum.",
        destination: "whatsapp",
      },
    ],
    optionalCtas: [],
    supportsCampaigns: true,
    supportsVideos: true,
    recommendedVideoCount: 2,
    customServiceLabel: "Özel Hizmet Ekle",
  },
  {
    id: "cleaning",
    label: "Temizlik",
    description: "Ev, ofis ve kurumsal temizlik hizmetleri.",
    serviceOptions: [
      "Ev Temizliği",
      "Ofis Temizliği",
      "İnşaat Sonrası Temizlik",
      "Villa Temizliği",
      "Residence Temizliği",
      "Plaza / Kurumsal Alan Temizliği",
      "Yazlık Ev Temizliği",
      "Taşınma Öncesi Temizlik",
      "Taşınma Sonrası Temizlik",
      "Koltuk Yıkama",
      "Yatak Yıkama",
      "Halı / Yerinde Halı Temizliği",
      "Cam Temizliği",
      "Buharlı Temizlik",
      "Detaylı Dip Köşe Temizlik",
      "Periyodik Temizlik",
      "Diğer / Özel Hizmet",
    ],
    minServices: 1,
    maxServices: 5,
    defaultCtas: [
      {
        id: "quote",
        label: "📋 Teklif İste",
        intent: "quote",
        prefill: "Merhaba, hizmetlerinizi inceledim. Temizlik hizmeti için teklif almak istiyorum.",
        destination: "whatsapp",
      },
      {
        id: "question",
        label: "💬 Soru Sormak İstiyorum",
        intent: "question",
        prefill: "Merhaba, temizlik hizmetleriniz hakkında birkaç soru sormak istiyorum.",
        destination: "whatsapp",
      },
    ],
    optionalCtas: [],
    supportsCampaigns: true,
    supportsVideos: true,
    recommendedVideoCount: 2,
    customServiceLabel: "Özel Hizmet Ekle",
  },
  {
    id: "event",
    label: "Davet & Organizasyon",
    description: "Düğün, nişan ve özel gün organizasyonları.",
    serviceOptions: [
      "Düğün",
      "Nişan",
      "Kına Gecesi",
      "Söz",
      "Nikah Sonrası Kutlama",
      "Doğum Günü",
      "Baby Shower",
      "Sünnet Organizasyonu",
      "Mezuniyet",
      "Kurumsal Etkinlik",
      "Toplantı / Lansman",
      "Özel Davet",
      "Salon Kiralama",
      "Organizasyon + Süsleme Paketi",
      "Catering / İkram",
      "Fotoğraf / Video Paketi",
      "Diğer / Özel Organizasyon",
    ],
    minServices: 1,
    maxServices: 5,
    defaultCtas: [
      {
        id: "appointment",
        label: "📅 Tarih / Uygunluk Sor",
        intent: "appointment",
        prefill: "Merhaba, organizasyon seçeneklerinizi inceledim. Planladığım tarih için müsaitlik ve bilgi almak istiyorum.",
        destination: "whatsapp",
      },
      {
        id: "quote",
        label: "💬 Teklif Al / Soru Sor",
        intent: "quote",
        prefill: "Merhaba, organizasyon paketleriniz ve fiyatlarınız hakkında bilgi almak istiyorum.",
        destination: "whatsapp",
      },
    ],
    optionalCtas: [],
    supportsCampaigns: true,
    supportsVideos: true,
    recommendedVideoCount: 2,
    customServiceLabel: "Özel Organizasyon Ekle",
  },
  {
    id: "education",
    label: "Eğitim / Kurs",
    description: "Kurs, özel ders ve sınav hazırlık programları.",
    serviceOptions: [
      "KPSS Hazırlık",
      "YKS Hazırlık",
      "LGS Hazırlık",
      "İngilizce Eğitimi",
      "Yabancı Dil Kursları",
      "Matematik Özel Ders",
      "Türkçe / Edebiyat",
      "Tarih / Coğrafya",
      "Online Eğitim",
      "Yüz Yüze Eğitim",
      "Deneme Sınavları",
      "Hafıza Teknikleri",
      "Soru Çözüm Programları",
      "Birebir Özel Ders",
      "Grup Dersleri",
      "Çocuk Eğitim Programları",
      "Yetişkin Eğitim Programları",
      "Mesleki Eğitim",
      "Diğer / Özel Eğitim",
    ],
    minServices: 1,
    maxServices: 5,
    defaultCtas: [
      {
        id: "info",
        label: "📚 Program / Eğitim Hakkında Bilgi Al",
        intent: "info",
        prefill: "Merhaba, eğitim programlarınızı inceledim. İçerik, süreç ve kayıt hakkında bilgi almak istiyorum.",
        destination: "whatsapp",
      },
      {
        id: "question",
        label: "💬 Soru Sormak İstiyorum",
        intent: "question",
        prefill: "Merhaba, eğitimleriniz hakkında birkaç soru sormak istiyorum.",
        destination: "whatsapp",
      },
    ],
    optionalCtas: [],
    supportsCampaigns: true,
    supportsVideos: true,
    recommendedVideoCount: 2,
    customServiceLabel: "Özel Eğitim Ekle",
  },
  {
    id: "realestate",
    label: "Emlak",
    description: "Konut, ticari gayrimenkul ve danışmanlık hizmetleri.",
    serviceOptions: [
      "Satılık Daire",
      "Kiralık Daire",
      "Satılık Villa",
      "Kiralık Villa",
      "Satılık Arsa",
      "Ticari Gayrimenkul",
      "Ofis / İş Yeri",
      "Yatırımlık Gayrimenkul",
      "Proje Satışları",
      "Sıfır Konut",
      "İkinci El Konut",
      "Lüks Gayrimenkul",
      "Yurt Dışı Gayrimenkul",
      "Gayrimenkul Danışmanlığı",
      "Ekspertiz / Değerleme Yönlendirmesi",
      "Diğer / Özel Portföy",
    ],
    minServices: 1,
    maxServices: 5,
    defaultCtas: [
      {
        id: "info",
        label: "🏠 Portföy Hakkında Bilgi Al",
        intent: "info",
        prefill: "Merhaba, portföylerinizi inceledim. İlgilendiğim gayrimenkul hakkında detaylı bilgi almak istiyorum.",
        destination: "whatsapp",
      },
      {
        id: "consultation",
        label: "📞 Danışmanla Görüş",
        intent: "consultation",
        prefill: "Merhaba, gayrimenkul seçenekleriniz hakkında bir danışmanla görüşmek istiyorum.",
        destination: "whatsapp",
      },
    ],
    optionalCtas: [],
    supportsCampaigns: true,
    supportsVideos: true,
    recommendedVideoCount: 2,
    customServiceLabel: "Özel Portföy Ekle",
  },
  {
    id: "food",
    label: "Yeme & İçme",
    description: "Restoran, kafe, catering ve paket servis.",
    serviceOptions: [
      "Ev Yemekleri",
      "Restoran",
      "Kafe",
      "Kahvaltı",
      "Fast Food",
      "Hamburger",
      "Pizza",
      "Kebap / Izgara",
      "Döner",
      "Tatlı / Pastane",
      "Börek / Fırın Ürünleri",
      "Catering",
      "Toplu Yemek",
      "Paket Servis",
      "Günlük Menü",
      "Özel Gün / Organizasyon Menüsü",
      "Yöresel Yemekler",
      "Diğer / Özel Menü",
    ],
    minServices: 1,
    maxServices: 5,
    defaultCtas: [
      {
        id: "info",
        label: "🍽️ Menü / Fiyat Bilgisi Al",
        intent: "info",
        prefill: "Merhaba, menülerinizi ve ürünlerinizi inceledim. Menü ve fiyatlar hakkında bilgi almak istiyorum.",
        destination: "whatsapp",
      },
      {
        id: "order",
        label: "🛵 Sipariş / Rezervasyon İçin İletişime Geç",
        intent: "order",
        prefill: "Merhaba, sipariş veya rezervasyon için bilgi almak istiyorum.",
        destination: "whatsapp",
      },
    ],
    optionalCtas: [],
    supportsCampaigns: true,
    supportsVideos: true,
    recommendedVideoCount: 2,
    customServiceLabel: "Özel Menü Ekle",
  },
  {
    id: "furniture",
    label: "Mobilya / Ev Dekorasyon",
    description: "Mobilya, özel ölçü ve ev dekorasyon ürünleri.",
    serviceOptions: [
      "Gardırop",
      "TV Ünitesi",
      "Çalışma Masası",
      "Komodin",
      "Yatak Odası Takımı",
      "Genç Odası",
      "Çocuk Odası",
      "Salon Mobilyası",
      "Yemek Odası",
      "Dresuar",
      "Kitaplık",
      "Mutfak Dolabı",
      "Özel Ölçü Mobilya",
      "Modüler Mobilya",
      "Dekoratif Ürünler",
      "Ev Aksesuarları",
      "Ofis Mobilyası",
      "Diğer / Özel Ürün",
    ],
    minServices: 1,
    maxServices: 5,
    defaultCtas: [
      {
        id: "info",
        label: "🛋️ Ürün / Fiyat Bilgisi Al",
        intent: "product_info",
        prefill: "Merhaba, ürünlerinizi inceledim. İlgilendiğim ürünler hakkında fiyat ve detaylı bilgi almak istiyorum.",
        destination: "whatsapp",
      },
      {
        id: "quote",
        label: "📐 Ölçü / Özel Tasarım İçin Teklif Al",
        intent: "quote",
        prefill: "Merhaba, özel ölçü veya tasarım için bilgi ve teklif almak istiyorum.",
        destination: "whatsapp",
      },
    ],
    optionalCtas: [],
    supportsCampaigns: true,
    supportsVideos: true,
    recommendedVideoCount: 2,
    customServiceLabel: "Özel Ürün Ekle",
  },
  {
    id: "network",
    label: "Network Marketing / Doğrudan Satış",
    description: "Ürün satışı ve iş / kazanç modeli.",
    serviceOptions: [
      "Cilt Bakım Ürünleri",
      "Gıda Takviye Ürünleri",
      "Sporcu Ürünleri",
      "Sağlıklı Yaşam Ürünleri",
      "İçecek Ürünleri",
      "Enerji Ürünleri",
      "Bitkisel Takviye Ürünleri",
      "Ev Ürünleri",
      "Online Kazanç Modeli",
      "Liderlik Programı",
      "Part Time İş Modeli",
      "Evden Çalışma Modeli",
    ],
    minServices: 1,
    maxServices: 5,
    defaultCtas: [
      {
        id: "info",
        label: "📦 Ürünler Hakkında Bilgi Al",
        intent: "product_info",
        prefill: "Merhaba, ürün kategorilerinizi inceledim. Ürünler ve kullanım seçenekleri hakkında detaylı bilgi almak istiyorum.",
        destination: "whatsapp",
      },
      {
        id: "business_model",
        label: "💼 İş / Kazanç Modelini Öğren",
        intent: "business_model",
        prefill: "Merhaba, online kazanç ve çalışma modelinizi inceledim. Sistem, başlangıç süreci ve detaylar hakkında bilgi almak istiyorum.",
        destination: "whatsapp",
      },
      {
        id: "question",
        label: "💬 Soru Sormak İstiyorum",
        intent: "question",
        prefill: "Merhaba, ürünleriniz ve çalışma modeliniz hakkında birkaç soru sormak istiyorum.",
        destination: "whatsapp",
      },
    ],
    optionalCtas: [],
    supportsCampaigns: true,
    supportsVideos: true,
    recommendedVideoCount: 2,
    customServiceLabel: "Özel Ürün Ekle",
  },
  {
    id: "onlinesales",
    label: "Online Satış / Instagram'dan Satış",
    description: "Giyim, aksesuar ve online ürün satışı.",
    serviceOptions: [
      "Kadın Giyim",
      "Erkek Giyim",
      "Çocuk Giyim",
      "Ayakkabı",
      "Çanta / Aksesuar",
      "Takı / Bijuteri",
      "Kozmetik / Kişisel Bakım",
      "Ev & Yaşam Ürünleri",
      "Dekorasyon Ürünleri",
      "Elektronik Aksesuar",
      "Hediyelik Ürünler",
      "El Yapımı / Tasarım Ürünler",
      "Anne & Bebek Ürünleri",
      "Spor / Fitness Ürünleri",
      "Hobi / Koleksiyon Ürünleri",
      "Diğer / Özel Ürün",
    ],
    minServices: 1,
    maxServices: 5,
    defaultCtas: [
      {
        id: "order",
        label: "🛒 Sipariş Vermek İstiyorum",
        intent: "order",
        prefill: "Merhaba, ürünlerinizi inceledim. Sipariş vermek istiyorum.",
        destination: "whatsapp",
      },
      {
        id: "info",
        label: "💬 Ürün Hakkında Bilgi Al",
        intent: "product_info",
        prefill: "Merhaba, ürünleriniz hakkında detaylı bilgi almak istiyorum.",
        destination: "whatsapp",
      },
      {
        id: "website",
        label: "🌐 Mağazayı / Ürünleri İncele",
        intent: "website",
        prefill: "",
        destination: "website",
      },
    ],
    optionalCtas: [],
    supportsCampaigns: true,
    supportsVideos: true,
    recommendedVideoCount: 2,
    customServiceLabel: "Özel Ürün Ekle",
  },
  {
    id: "localproducts",
    label: "Yöresel Ürünler Satış",
    description: "Yöresel ve doğal gıda ürünleri satışı.",
    serviceOptions: [
      "Peynir Çeşitleri",
      "Zeytin / Zeytinyağı",
      "Bal / Arı Ürünleri",
      "Reçel / Marmelat",
      "Salça / Soslar",
      "Tarhana / Erişte / Makarna",
      "Kurutulmuş Ürünler",
      "Baharatlar",
      "Kuruyemiş",
      "Lokum / Tatlı Ürünleri",
      "Pekmez / Tahin",
      "Yöresel Kahvaltılık Ürünler",
      "Et / Şarküteri Ürünleri",
      "Organik / Doğal Ürünler",
      "Hediye / Yöresel Paketler",
      "Karma Yöresel Ürün Paketi",
      "Diğer / Özel Ürün",
    ],
    minServices: 1,
    maxServices: 5,
    defaultCtas: [
      {
        id: "order",
        label: "🛒 Sipariş Vermek İstiyorum",
        intent: "order",
        prefill: "Merhaba, yöresel ürünlerinizi inceledim. Sipariş vermek istiyorum.",
        destination: "whatsapp",
      },
      {
        id: "info",
        label: "📦 Ürün / Kargo Bilgisi Al",
        intent: "product_info",
        prefill: "Merhaba, ürünleriniz, paket seçenekleri ve kargo hakkında detaylı bilgi almak istiyorum.",
        destination: "whatsapp",
      },
      {
        id: "question",
        label: "💬 Soru Sormak İstiyorum",
        intent: "question",
        prefill: "Merhaba, yöresel ürünleriniz hakkında birkaç soru sormak istiyorum.",
        destination: "whatsapp",
      },
    ],
    optionalCtas: [],
    supportsCampaigns: true,
    supportsVideos: true,
    recommendedVideoCount: 2,
    customServiceLabel: "Özel Ürün Ekle",
  },
  {
    id: "professional",
    label: "Profesyonel Hizmetler",
    description: "Danışmanlık, hukuk, muhasebe ve kreatif hizmetler.",
    serviceOptions: [
      "Danışmanlık",
      "Hukuk / Avukatlık Hizmetleri",
      "Mali Müşavirlik / Muhasebe",
      "Sigorta Hizmetleri",
      "İnsan Kaynakları / İşe Alım",
      "Kurumsal Eğitim / Danışmanlık",
      "Dijital Pazarlama",
      "Sosyal Medya Yönetimi",
      "Reklam Yönetimi",
      "Web Tasarım / Yazılım",
      "Grafik Tasarım / Kreatif Hizmetler",
      "Fotoğraf / Video Prodüksiyon",
      "Mimarlık / İç Mimarlık",
      "Mühendislik Hizmetleri",
      "Gayrimenkul Danışmanlığı",
      "İş Geliştirme / Strateji",
      "Koçluk / Mentorluk",
      "Tercüme / Çeviri",
      "Organizasyon / Etkinlik Hizmetleri",
      "Diğer / Özel Profesyonel Hizmet",
    ],
    minServices: 1,
    maxServices: 5,
    defaultCtas: [
      {
        id: "quote",
        label: "📋 Teklif İste",
        intent: "quote",
        prefill: "Merhaba, hizmetlerinizi inceledim. İhtiyacım için teklif ve detaylı bilgi almak istiyorum.",
        destination: "whatsapp",
      },
      {
        id: "consultation",
        label: "📞 Görüşme Talep Et",
        intent: "consultation",
        prefill: "Merhaba, hizmetleriniz hakkında kısa bir görüşme yapmak istiyorum. Uygun olduğunuz zamanı paylaşabilir misiniz?",
        destination: "whatsapp",
      },
      {
        id: "question",
        label: "💬 Soru Sormak İstiyorum",
        intent: "question",
        prefill: "Merhaba, hizmetleriniz hakkında birkaç soru sormak istiyorum.",
        destination: "whatsapp",
      },
    ],
    optionalCtas: [],
    supportsCampaigns: true,
    supportsVideos: true,
    recommendedVideoCount: 2,
    customServiceLabel: "Özel Profesyonel Hizmet Ekle",
  },
  {
    id: "other",
    label: "Diğer / Genel Hizmet",
    description: "Diğer tüm hizmet türleri için esnek yapı.",
    serviceOptions: [
      "Bireysel Hizmet",
      "Kurumsal Hizmet",
      "Yerinde Hizmet",
      "Online Hizmet",
      "Randevulu Hizmet",
      "Teklif Usulü Hizmet",
      "Proje Bazlı Hizmet",
      "Periyodik Hizmet",
      "Satış Sonrası Hizmet",
      "Teknik / Uygulamalı Hizmet",
      "Diğer / Özel Hizmet",
    ],
    minServices: 1,
    maxServices: 5,
    defaultCtas: [
      {
        id: "quote",
        label: "📋 Teklif İste",
        intent: "quote",
        prefill: "Merhaba, hizmetlerinizi inceledim. İhtiyacım için teklif almak istiyorum.",
        destination: "whatsapp",
      },
      {
        id: "question",
        label: "💬 Soru Sor",
        intent: "question",
        prefill: "Merhaba, hizmetleriniz hakkında birkaç soru sormak istiyorum.",
        destination: "whatsapp",
      },
      {
        id: "consultation",
        label: "📞 Görüşme Talep Et",
        intent: "consultation",
        prefill: "Merhaba, hizmetleriniz hakkında görüşme yapmak istiyorum.",
        destination: "whatsapp",
      },
    ],
    optionalCtas: [],
    supportsCampaigns: true,
    supportsVideos: true,
    recommendedVideoCount: 2,
    customServiceLabel: "Özel Hizmet Ekle",
  },
];

/* ---------------- Yardımcılar ---------------- */

export const OTHER_SECTOR_ID = "other";

export function findSectorConfig(sektor: string): SectorConfig | undefined {
  return SECTOR_CONFIGS.find((c) => c.label === sektor);
}

export function getServiceOptions(config: SectorConfig): string[] {
  return config.serviceOptions.filter((o) => !o.startsWith("Diğer"));
}

/* Sektör bazlı semantic item label (çoğul form, UI wording için).
   "hizmetler" / "ürün kategorileri" / "programlar" gibi. */
const ITEM_LABELS: Record<string, string> = {
  beauty: "hizmetler",
  cleaning: "hizmetler",
  event: "organizasyonlar",
  education: "programlar",
  realestate: "portföyler",
  food: "menüler",
  furniture: "ürünler",
  network: "ürünler",
  onlinesales: "ürün kategorileri",
  localproducts: "ürünler",
  professional: "hizmetler",
  other: "hizmetler",
};

export function getItemLabel(config: SectorConfig): string {
  return ITEM_LABELS[config.id] ?? "hizmetler";
}